import { getValidatedLimitParam, getValidatedPageParam } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

export interface ICrmBrandContactDetailsData {
  id: number;
  contactName: string;
  designation?: string;
  phoneNumber?: string;
  email?: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  brand: {
    id: number;
    name: string;
    image?: string;
  };
}

export interface ICrmBrandContactDetailsResponse {
  data: ICrmBrandContactDetailsData[];
  total: number;
  pageCount: number;
  limit: number;
  page: number;
  sort: string;
}

const endpoint = 'ums/v1/crm/management/brands/contacts';
export const getCrmBrandContactDetailsEndpointIdentifier = endpoint;

type TData = ICrmBrandContactDetailsResponse;
type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    brandId?: TParam;
    phoneNumber?: TParam;
    email?: TParam;
    isVerified?: TParam;
    sort?: TParam;
  }
];

type Options = Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCrmBrandContactDetailsApi(options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { page, limit, brandId, phoneNumber, email, isVerified, sort } = getPageParams();

  const queryKey: TQueryKey = [
    endpoint,
    {
      page,
      limit,
      brandId,
      phoneNumber,
      email,
      isVerified,
      sort,
    },
  ];

  const queryFn = async () => {
    const params: any = {
      page: getValidatedPageParam(),
      limit: getValidatedLimitParam(),
    };

    if (brandId) {
      params.brandId = Number(brandId);
    }

    if (phoneNumber) {
      params.phoneNumber = String(phoneNumber);
    }

    if (email) {
      params.email = String(email);
    }

    if (isVerified !== undefined && isVerified !== null) {
      params.isVerified = Boolean(isVerified);
    }

    if (sort) {
      params.sort = String(sort);
    }

    const { data } = await http.get(endpoint, { params });
    return data;
  };

  return useQueryWrapper(queryKey, queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
